// The open tournament and NSF: which of its players are paid-up NSF members, and whether its
// results have gone to NSF. The answers are kept for the screens that show them: the
// roster, the pending registrations, the Statistics window and the tournament's settings.
//
// The membership answer is kept for the open tournament only, in memory, and never saved
// anywhere: it is NSF's data about people, and it is asked for again whenever the Players
// page is opened (js/ui/navigation.js). Only groups with "NSF-medlemskap kreves" switched
// on are checked — on the terms of the series beside it, NGP, BGP or any other
// (js/utils/nsf-membership.js) — and only by the tournament's own arbiters. See
// functions/lib/nsf-api.js for what the server sends and to whom.
//
// What has been sent to NSF is nsfReports/{tournamentId}, which only the server writes:
// the app reads it (firestore.rules lets the tournament's arbiters) and asks nsfApi to send.
//
// Screens do not import each other through here: each registers a redraw with
// onNsfCheckChange(), and is called when an answer arrives, results go to NSF or a
// player's details change.

import { state } from '../state.js';
import { callFunction } from './functions-api.js';
import { db, doc, onSnapshot } from './firebase-core.js';
import { nsfRequiredGroupIds, nsfSeriesInUse } from '../utils/nsf-membership.js';

// Opening the Players page twice in quick succession asks once.
const MIN_INTERVAL_MS = 15 * 1000;

// THE SWITCH, THE BADGES AND THE CHECK ARE FOR EVERY ARBITER, of their own tournaments
// (2026-10-14: NSF lets any tournament require membership by itself, and NGP and BGP
// tournaments are marked so by their turneringsleder). CHECK_ADMINS_ONLY in
// functions/lib/nsf-api.js is the server's half.
//
// THE NSF HALF OF THE PLAYER SEARCH (js/ui/player-search.js) is still for administrators
// only: it finds anyone in the register, not just a tournament's own entries. The server
// refuses everyone else regardless (SEARCH_ADMINS_ONLY in functions/lib/nsf-api.js); open
// both together, once NSF has agreed to it.
export const NSF_SEARCH_ADMINS_ONLY = true;

/** Is the NSF check offered to whoever is signed in? */
export function nsfCheckAvailable() {
    const auth = window.authApp;
    return !!(auth && auth.getCurrentUser && auth.getCurrentUser());
}

// status: 'off' (nothing to check here) | 'loading' | 'ready' | 'error'
let current = { tournamentId: null, status: 'off', data: null, error: null, at: 0 };
let inFlight = null;
const listeners = new Set();

export function onNsfCheckChange(fn) {
    listeners.add(fn);
}

/** Tell every screen to redraw: an answer arrived, or a player's details changed. */
export function notifyNsfChange() {
    listeners.forEach(fn => {
        try { fn(); } catch (e) { console.error('NSF redraw failed:', e); }
    });
}

function activeTournamentId() {
    return state.activeTournament && state.activeTournament.id ? String(state.activeTournament.id) : null;
}

// Only an arbiter the check is offered to, with a tournament open in which some group
// requires membership, has anything to ask.
function checkable() {
    if (state.isSpectatorMode || !state.activeTournament) return false;
    if (nsfRequiredGroupIds(state.activeTournament).length === 0) return false;
    return nsfCheckAvailable();
}

// ----- the results sent to NSF ----------------------------------------------------------

// A listener that failed — the tournament not saved to the cloud yet, or no longer an
// arbiter's — is tried again no sooner than this.
const REPORT_RETRY_MS = 30 * 1000;

let reportWatch = { tournamentId: null, report: null, unsubscribe: null, failedAt: 0 };

function stopReportWatch() {
    if (reportWatch.unsubscribe) {
        try { reportWatch.unsubscribe(); } catch (e) { /* already gone */ }
    }
    reportWatch = { tournamentId: null, report: null, unsubscribe: null, failedAt: 0 };
}

function setReport(tournamentId, report) {
    if (reportWatch.tournamentId !== tournamentId) return;
    const before = JSON.stringify(reportWatch.report);
    reportWatch.report = report || null;
    if (JSON.stringify(reportWatch.report) !== before) notifyNsfChange();
}

/** What the open tournament has sent NSF (nsfReports/{id}): { files, sentAt, sentBy }, or null. */
export function nsfReportNow() {
    const id = activeTournamentId();
    return id && reportWatch.tournamentId === id ? reportWatch.report : null;
}

/**
 * Listen to what the open tournament has sent NSF, and stop listening to the last one's.
 * Only a tournament with an NGP or BGP group sends anything, so only one listens; called
 * when its settings are drawn, and cheap when nothing has changed.
 */
export function syncNsfReportWatch() {
    const t = state.activeTournament;
    const id = state.isSpectatorMode ? null : activeTournamentId();
    if (!id || !nsfCheckAvailable() || nsfSeriesInUse(t).length === 0) {
        if (reportWatch.tournamentId) stopReportWatch();
        return;
    }
    if (reportWatch.tournamentId === id
        && (reportWatch.unsubscribe || Date.now() - reportWatch.failedAt < REPORT_RETRY_MS)) return;

    const keep = reportWatch.tournamentId === id ? reportWatch.report : null;
    stopReportWatch();
    reportWatch = { tournamentId: id, report: keep, unsubscribe: null, failedAt: 0 };
    try {
        reportWatch.unsubscribe = onSnapshot(doc(db, 'nsfReports', id),
            snap => setReport(id, snap.exists() ? snap.data() : null),
            () => {
                // Refused: the tournament is not in the cloud yet, or this account is not
                // its arbiter. Nothing to show; asked again later.
                if (reportWatch.tournamentId !== id) return;
                reportWatch.unsubscribe = null;
                reportWatch.failedAt = Date.now();
            });
    } catch (e) {
        reportWatch.failedAt = Date.now();
    }
}

/**
 * Send NSF the open tournament's results: [{ name, content }] — TRF from its NGP groups,
 * TRX from its BGP groups (js/services/tournament-io.js buildNsfResultFiles). Only the
 * owner may (functions/lib/nsf-api.js). Never throws.
 */
export async function sendNsfResults(files) {
    const id = activeTournamentId();
    if (!id) return { ok: false, error: 'no_tournament' };
    const res = await callFunction('nsfApi', { action: 'report', tournamentId: id, files });
    if (res.ok && res.report) setReport(id, res.report);
    return res;
}

/** The check as it stands for the open tournament: { status, data, error }. */
export function nsfCheckState() {
    const id = activeTournamentId();
    return current.tournamentId === id ? current : { tournamentId: id, status: 'off', data: null, error: null, at: 0 };
}

/**
 * Ask the server again. `force` asks even when there is an answer already, which is what
 * opening the Players page does — at most once per MIN_INTERVAL_MS, unless `immediate`
 * says the arbiter asked in so many words ("Check again", a badge not checked yet).
 * Without `force`, an answer for this tournament is kept.
 * Resolves to nsfCheckState(). Never throws: a failure is a state, shown as such.
 */
export async function refreshNsfCheck({ force = false, immediate = false } = {}) {
    const id = activeTournamentId();
    if (!checkable()) {
        if (current.status !== 'off' || current.tournamentId !== id) {
            current = { tournamentId: id, status: 'off', data: null, error: null, at: 0 };
            notifyNsfChange();
        }
        return current;
    }
    if (inFlight && inFlight.id === id) return inFlight.promise;
    const sameTournament = current.tournamentId === id;
    if (sameTournament && current.status === 'ready') {
        if (!force) return current;
        if (!immediate && Date.now() - current.at < MIN_INTERVAL_MS) return current;
    }

    // Keep showing the last answer while the new one is on its way.
    const previous = sameTournament ? current.data : null;
    current = { tournamentId: id, status: 'loading', data: previous, error: null, at: sameTournament ? current.at : 0 };
    notifyNsfChange();

    const promise = (async () => {
        const res = await callFunction('nsfApi', { action: 'check', tournamentId: id });
        // The arbiter has opened another tournament meanwhile: this answer is not for it.
        if (activeTournamentId() !== id) return nsfCheckState();
        if (res.ok && res.required === false) {
            // The server's copy of the tournament does not require membership yet: the
            // switch was turned on and the save has not reached it. ensureNsfCheck asks
            // again after a pause, not on every redraw.
            current = { tournamentId: id, status: 'off', serverOff: true, data: null, error: null, at: Date.now() };
        } else if (res.ok) {
            current = { tournamentId: id, status: 'ready', data: res, error: null, at: Date.now() };
        } else {
            current = { tournamentId: id, status: 'error', data: previous, error: res.error || 'network', at: Date.now() };
        }
        notifyNsfChange();
        return current;
    })().finally(() => { inFlight = null; });
    inFlight = { id, promise };
    return promise;
}

/**
 * Called on every roster render: asks only when there is no answer for the open
 * tournament yet (another tournament was opened, or the switch was just turned on), and
 * forgets the answer when there is nothing to check any more.
 */
export function ensureNsfCheck() {
    const id = activeTournamentId();
    if (!checkable()) {
        if (current.status !== 'off') refreshNsfCheck();
        return;
    }
    if (current.tournamentId !== id) { refreshNsfCheck(); return; }
    if (current.status !== 'off') return;
    if (!current.serverOff || Date.now() - current.at >= MIN_INTERVAL_MS) refreshNsfCheck();
}

/**
 * One player's result: undefined when there is no check to show (nothing required, or no
 * answer yet), null when the check ran but did not include them — added since the
 * roster was last saved. A player who came in through a pending registration is found
 * by that registration until the next check knows them as a player.
 */
export function nsfResultForPlayer(player) {
    const s = nsfCheckState();
    if (!player || !s.data) return undefined;
    const d = s.data;
    return (d.players && d.players[String(player.id)])
        || (player.registrationId && d.registrations && d.registrations[String(player.registrationId)])
        || null;
}

/** One pending registration's result, with the same undefined/null meaning. */
export function nsfResultForRegistration(registrationId) {
    const s = nsfCheckState();
    if (!s.data) return undefined;
    return (s.data.registrations && s.data.registrations[String(registrationId)]) || null;
}

// ----- members picked in the player search -----------------------------------------------
//
// A player picked out of NSF's register in the player search (js/ui/player-search.js) is
// one the register has just shown: their badge says what it showed straight away, instead
// of "not checked yet" until the next check reads the saved roster. Kept like the check's
// answer, in memory and for the open tournament only, and by NSF ID, so a player whose
// NSF ID is changed by hand no longer has it. A check that has seen the player is the
// newer word (js/ui/nsf-membership-ui.js).
let picks = { tournamentId: null, years: new Map() };

/** Remember a member just picked from the search: their NSF ID and the year they have paid for. */
export function rememberNsfPick(member) {
    const id = activeTournamentId();
    const nsfId = String((member && member.nsfId) || '').trim();
    const year = parseInt(member && member.membershipYear, 10);
    if (!id || !nsfId || !(year > 0)) return;
    if (picks.tournamentId !== id) picks = { tournamentId: id, years: new Map() };
    picks.years.set(nsfId, year);
}

/** The year the register showed this player paid for, when they were picked from the search; else null. */
export function nsfPickedYear(player) {
    const nsfId = String((player && player.nsfId) || '').trim();
    if (!nsfId || picks.tournamentId !== activeTournamentId()) return null;
    return picks.years.get(nsfId) || null;
}

/** The year a group's membership must be paid for, as the server decided it. */
export function nsfYearForGroup(groupId) {
    const s = nsfCheckState();
    const groups = (s.data && Array.isArray(s.data.groups)) ? s.data.groups : [];
    const key = groupId === undefined || groupId === null ? null : String(groupId);
    const g = groups.find(x => x.groupId === key) || groups[0];
    return g ? g.year : new Date().getFullYear();
}
